/**
 * #748 — how one Polymarket order's cancel lifecycle reads in a row.
 *
 * The fence in `bulkOrderCancel` decides what may be SENT; this decides what a
 * row SAYS. It exists as one shared function because the same order is rendered
 * by several mounts at once (Position Sidebar, Position Tracker, the reusable
 * Polymarket orders panel), and the defect #748 reports is precisely those
 * mounts disagreeing about the state of one order. Three inlined copies of this
 * ladder would be three chances to drift back apart.
 *
 * Pure, React-free and venue-local — no timers, no store, no network.
 */
import { isPendingPolyCancelPhase, type PolyCancelState } from './bulkOrderCancel';

export interface PolyCancelRowState {
  /** The row's cancel control is disabled exactly while this is true, and it
   *  comes from `isPendingPolyCancelPhase` — the fence's own rule — rather than
   *  from a second reading of the phase here. Whether another DELETE may go out
   *  has exactly one authority; a copy of that rule in the presentation layer
   *  would look right and drift later. */
  pending: boolean;
  /** Concise in-row label, or null when the row shows its ordinary control. */
  label: string | null;
  /** The full sentence for `title`/`aria-label`, or null when ordinary. */
  message: string | null;
  /** Terminal, unconfirmed, and retryable. The control is live again, but it
   *  must NOT look untouched — that ambiguity is the #748 complaint. */
  unconfirmed: boolean;
}

const ORDINARY: PolyCancelRowState = Object.freeze({
  pending: false,
  label: null,
  message: null,
  unconfirmed: false,
});

/**
 * `state` is this order's entry from `getPolyCancelSnapshot()` — `undefined`
 * when no cancel is in play, which is the overwhelmingly common case.
 */
export function describePolyCancelRow(state: PolyCancelState | undefined): PolyCancelRowState {
  // No cancel in play — the overwhelmingly common case, and the only legitimate
  // reason to fall through to the ordinary control.
  if (state === undefined) return ORDINARY;
  switch (state.phase) {
    case 'requesting':
      // The DELETE is in flight. Nothing has been accepted yet, so this claims
      // nothing about the venue.
      return {
        pending: isPendingPolyCancelPhase(state.phase),
        label: 'Canceling…',
        message: 'Cancel request in flight for this Polymarket order.',
        unconfirmed: false,
      };
    case 'awaiting-removal':
      // A 2xx came back. Deliberately NOT "Canceled": the venue accepted a
      // request, and removal is only proven when Open Orders stops listing the
      // ID. Same doctrine the bulk report follows.
      return {
        pending: isPendingPolyCancelPhase(state.phase),
        label: 'Refreshing status…',
        message:
          'Cancel submitted — waiting for Open Orders to confirm this Polymarket order is gone.',
        unconfirmed: false,
      };
    case 'timed-out':
      // The control comes back deliberately (see `isPolyCancelPending`), but
      // labelled, because an ordinary-looking Cancel here would be the original
      // bug wearing a different hat: the trader could not tell "never clicked"
      // from "asked, and we cannot confirm".
      return {
        pending: isPendingPolyCancelPhase(state.phase),
        label: 'Retry cancel',
        message: state.message ?? 'Cancel could not be confirmed. Retry when ready.',
        unconfirmed: true,
      };
    default: {
      // ⚠️ Exhaustive on purpose. A phase added to the fence WITHOUT a row
      // treatment here would otherwise fall through and render as an ordinary,
      // fully clickable cancel button on a money-adjacent control — silently,
      // and only for the state nobody had thought about yet. This makes that a
      // compile error instead. The runtime fallback stays safe for a payload
      // from an older build.
      const unhandled: never = state.phase;
      void unhandled;
      return ORDINARY;
    }
  }
}
